import { useEffect, useState } from "react";
import { api, HEADS, HEAD_COLORS } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Legend } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DrilldownModal({ pair, train, week, onClose }) {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [headFilter, setHeadFilter] = useState("all");

  useEffect(() => {
    if (!pair && !train) return;
    const drillParams = pair
      ? { pair_code: pair.pair_code, week_label: week }
      : { train_no: train.train_no, week_label: week };
    const trendUrl = pair
      ? `/trends/${encodeURIComponent(pair.pair_code)}`
      : `/train-trends/${encodeURIComponent(train.train_no)}`;
    (async () => {
      const [d, t] = await Promise.all([
        api.get("/drilldown", { params: drillParams }),
        api.get(trendUrl, { params: { weeks: 8 } }),
      ]);
      setData(d.data);
      setTrend(t.data);
    })();
  }, [pair, train, week]);

  const title = pair ? pair.pair_code : `Train ${train?.train_no}`;
  const subtitle = pair ? pair.pair_name : (train?.pair_name || "Not in pair master");
  const anyAlert = pair ? pair.any_alert : train?.any_alert;

  return (
    <Dialog open={!!(pair || train)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="drilldown-modal" className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <span className="font-mono text-blue-700">{title}</span>
            <span className="text-slate-800">{subtitle}</span>
            {anyAlert && <Badge className="bg-red-100 text-red-700 border border-red-200">Alert</Badge>}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">Complaint drilldown for {title} — head, coach, date and raw rows</DialogDescription>

        {!data && <Skeleton className="h-40 w-full" />}
        {data && (
          <div className="space-y-6">
            {/* Head breakdown: each head box holds its dates + coaches */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">By Complaint Head — {week}</div>
                <Select value={headFilter} onValueChange={setHeadFilter}>
                  <SelectTrigger data-testid="drilldown-head-filter" className="h-8 w-[190px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Heads</SelectItem>
                    {HEADS.map((h) => <SelectItem key={h} value={h} data-testid={`drilldown-head-option-${h.replace(/\s+/g, "-").toLowerCase()}`}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className={`grid grid-cols-1 ${headFilter === "all" ? "md:grid-cols-2" : ""} gap-3`}>
                {HEADS.filter((h) => headFilter === "all" || h === headFilter).map((h) => {
                  const det = (data.by_head_detail || {})[h] || { dates: [], coaches: [] };
                  return (
                    <div
                      key={h}
                      data-testid={`head-card-${h.replace(/\s+/g, "-").toLowerCase()}`}
                      className="p-3 rounded-lg border"
                      style={{ background: HEAD_COLORS[h].bg }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: HEAD_COLORS[h].text }}>{h}</div>
                        <div className="font-mono font-bold text-xl" style={{ color: HEAD_COLORS[h].text }}>{data.by_head[h] || 0}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2.5">
                        <div>
                          <div className="text-[9px] uppercase font-semibold tracking-wider opacity-60 mb-1" style={{ color: HEAD_COLORS[h].text }}>Dates</div>
                          <div className="flex flex-wrap gap-1">
                            {det.dates.length === 0 && <span className="text-[10px] text-slate-400">—</span>}
                            {det.dates.map((d) => (
                              <span
                                key={d.date}
                                data-testid={`head-date-${d.date}`}
                                className="px-1.5 py-0.5 rounded bg-white/80 text-[10px] font-mono"
                                style={{ color: HEAD_COLORS[h].text }}
                              >
                                {d.date.slice(5) || d.date} <b>×{d.count}</b>
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase font-semibold tracking-wider opacity-60 mb-1" style={{ color: HEAD_COLORS[h].text }}>Coaches</div>
                          <div className="flex flex-wrap gap-1">
                            {det.coaches.length === 0 && <span className="text-[10px] text-slate-400">—</span>}
                            {det.coaches.map((c) => (
                              <span
                                key={c.coach}
                                data-testid={`head-coach-${String(c.coach).replace(/\.0$/, "")}`}
                                className="px-1.5 py-0.5 rounded bg-white/80 text-[10px] font-mono"
                                style={{ color: HEAD_COLORS[h].text }}
                              >
                                {String(c.coach).replace(/\.0$/, "")} <b>×{c.count}</b>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 8-week trend */}
            {trend && (
              <div>
                <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">8-Week Trend</div>
                <div className="h-56 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trend.series}>
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {HEADS.map((h) => (
                        <Bar key={h} dataKey={h} stackId="a" fill={HEAD_COLORS[h].stroke} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
